import { Fragment, useEffect, useMemo, useState } from "react";
import RichText from "../RichText/RichText";
import Input from "./Input/Input";
import {
  buildFieldDefinitions,
  createInitialValues,
  createSubmittedFields,
  validateFields,
} from "./formFields";
import { createFormRequest } from "./formRequest";
import "./Form.css";

const EMPTY_STATUS = { type: "", message: "" };
const MARKETING_CONSENT_TEXT =
  "أوافق على تلقّي رسائل تتضمن محتوى وتحديثات وعروضًا تسويقية بشأن خدمات حسين نديم، عبر البريد الإلكتروني أو WhatsApp أو SMS أو وسائل تواصل أخرى أقدّمها. يمكنني إلغاء موافقتي في أي وقت.";
const REGISTRATION_NOTICE_TEXT =
  "سنستخدم اسمك وبريدك الإلكتروني لتأكيد التسجيل وإرسال رابط Zoom وتذكيرات هذا اللقاء.";

function isConditionsInput(component) {
  return (
    component?.name === "input" &&
    String(component.type || "text").toLowerCase() === "conditions"
  );
}

function isMarketingConsentInput(component) {
  if (component?.name !== "input") {
    return false;
  }

  const configuredName = String(
    component.field || component.fieldName || component.key || component.id || ""
  )
    .trim()
    .toLowerCase();

  return configuredName === "marketingconsent";
}

function addMarketingConsentInput(components) {
  if (
    !components.some(isConditionsInput) ||
    components.some(isMarketingConsentInput)
  ) {
    return components;
  }

  const conditionsIndex = components.findIndex(isConditionsInput);
  const marketingConsentInput = {
    name: "input",
    field: "marketingConsent",
    text: MARKETING_CONSENT_TEXT,
    type: "checkbox",
    required: false,
  };

  return [
    ...components.slice(0, conditionsIndex + 1),
    marketingConsentInput,
    ...components.slice(conditionsIndex + 1),
  ];
}

function Form({ api, components = [], onSuccess }) {
  const formComponents = useMemo(
    () => addMarketingConsentInput(components),
    [components]
  );
  const fields = useMemo(
    () => buildFieldDefinitions(formComponents),
    [formComponents]
  );
  const fieldsByComponentIndex = useMemo(
    () =>
      new Map(fields.map((field) => [field.componentIndex, field])),
    [fields]
  );
  const [values, setValues] = useState(() => createInitialValues(fields));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setValues(createInitialValues(fields));
    setErrors({});
    setStatus(EMPTY_STATUS);
  }, [fields]);

  const handleFieldChange = (fieldName, value) => {
    setValues((currentValues) => ({
      ...currentValues,
      [fieldName]: value,
    }));

    setErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(EMPTY_STATUS);

    const validationErrors = validateFields(fields, values);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const submittedFields = createSubmittedFields(fields, values);

    setIsSubmitting(true);

    try {
      const request = createFormRequest(api, submittedFields);
      const response = await fetch(request.url, request.options);
      const responseText = await response.text();
      let responseData = {};

      try {
        responseData = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        if (!response.ok) {
          throw new Error(
            "تعذر الاتصال بخادم التسجيل. يرجى تشغيل الخادم ثم المحاولة مرة أخرى."
          );
        }

        throw new Error("استجابة غير صالحة من خادم التسجيل.");
      }

      if (!response.ok) {
        throw new Error(
          responseData.error || responseData.message || "تعذر إتمام التسجيل."
        );
      }

      setValues(createInitialValues(fields));

      if (onSuccess) {
        onSuccess({
          fields: submittedFields,
          response: responseData,
        });
        return;
      }
      setStatus({
        type: "success",
        message: responseData.message || "تم التسجيل بنجاح.",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "حدث خطأ أثناء التسجيل.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="lp-form" noValidate onSubmit={handleSubmit}>
      {formComponents.map((component, componentIndex) => {
        if (component?.name === "richtext") {
          return <RichText {...component} key={componentIndex} />;
        }

        if (component?.name === "input") {
          const field = fieldsByComponentIndex.get(componentIndex);

          return field ? (
            <Input
              error={errors[field.fieldName]}
              field={field}
              key={componentIndex}
              onChange={(value) =>
                handleFieldChange(field.fieldName, value)
              }
              value={values[field.fieldName]}
            />
          ) : null;
        }

        if (component?.name === "button") {
          return (
            <Fragment key={componentIndex}>
              <button
                className="lp-form-submit"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting
                  ? component.loadingText || "جارٍ التسجيل..."
                  : component.text || component.value || "سجل الآن"}
              </button>
              <p className="lp-form-registration-notice">
                {REGISTRATION_NOTICE_TEXT}
              </p>
            </Fragment>
          );
        }

        return null;
      })}

      {status.message && (
        <div className={`lp-form-status lp-form-status-${status.type}`}>
          {status.message}
        </div>
      )}
    </form>
  );
}

export default Form;
